/**
 * Share utilities for generating and exporting share images
 */

import html2canvas from 'html2canvas';

/**
 * Generate a PNG blob from an HTML element as a 1:1 square image
 */
export async function generateShareImage(element: HTMLElement): Promise<Blob> {
    // Square dimensions: 1080 x 1080 (1:1)
    const squareSize = 1080;

    const canvas = await html2canvas(element, {
        backgroundColor: '#F5F3F0', // Match brand background
        scale: 2, // Higher resolution for sharing
        useCORS: true,
        logging: false,
    });

    // Create a new canvas with square dimensions
    const squareCanvas = document.createElement('canvas');
    squareCanvas.width = squareSize;
    squareCanvas.height = squareSize;
    const ctx = squareCanvas.getContext('2d')!;

    // Fill background
    ctx.fillStyle = '#F5F3F0';
    ctx.fillRect(0, 0, squareSize, squareSize);

    // Calculate scaling to fit content centered
    const contentScale = Math.min(
        (squareSize * 0.9) / canvas.width,
        (squareSize * 0.9) / canvas.height
    );
    const scaledWidth = canvas.width * contentScale;
    const scaledHeight = canvas.height * contentScale;
    const x = (squareSize - scaledWidth) / 2;
    const y = (squareSize - scaledHeight) / 2;

    // Draw the content centered
    ctx.drawImage(canvas, x, y, scaledWidth, scaledHeight);

    return new Promise((resolve, reject) => {
        squareCanvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to generate image'));
            }
        }, 'image/png', 1.0);
    });
}

/**
 * Generate a 9:16 aspect ratio image for Instagram Stories
 */
export async function generateInstagramStoryImage(element: HTMLElement): Promise<Blob> {
    // Instagram Story dimensions: 1080 x 1920 (9:16)
    const storyWidth = 1080;
    const storyHeight = 1920;

    // First capture the element
    const canvas = await html2canvas(element, {
        backgroundColor: '#F5F3F0',
        scale: 2,
        useCORS: true,
        logging: false,
    });

    // Create a new canvas with Story dimensions
    const storyCanvas = document.createElement('canvas');
    storyCanvas.width = storyWidth;
    storyCanvas.height = storyHeight;
    const ctx = storyCanvas.getContext('2d')!;

    // Fill background
    ctx.fillStyle = '#F5F3F0';
    ctx.fillRect(0, 0, storyWidth, storyHeight);

    // Calculate scaling to fit content centered
    const contentScale = Math.min(
        (storyWidth * 0.9) / canvas.width,
        (storyHeight * 0.8) / canvas.height
    );
    const scaledWidth = canvas.width * contentScale;
    const scaledHeight = canvas.height * contentScale;
    const x = (storyWidth - scaledWidth) / 2;
    const y = (storyHeight - scaledHeight) / 2;

    // Draw the content centered
    ctx.drawImage(canvas, x, y, scaledWidth, scaledHeight);

    return new Promise((resolve, reject) => {
        storyCanvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to generate Instagram Story image'));
            }
        }, 'image/png', 1.0);
    });
}

/**
 * Download a blob as a file
 */
export function downloadImage(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Download for Instagram (9:16 Story format)
 */
export function downloadForInstagram(blob: Blob): void {
    downloadImage(blob, 'finite-life-story.png');
    showToast('Image downloaded! Share it to your Instagram Story.');
}

/**
 * Share to X (Twitter) - opens compose dialog
 */
export function shareToX(blob: Blob): void {
    // X doesn't support direct image upload via URL
    // Download the image and open compose dialog with text
    downloadImage(blob, 'finite-life-weeks.png');
    const text = encodeURIComponent('Visualize your life in weeks → finite.eleventh-photon.com');
    const url = `https://twitter.com/intent/tweet?text=${text}`;
    window.open(url, '_blank', 'width=550,height=420');
    showToast('Image downloaded! Attach it to your post on X.');
}

/**
 * Share to LinkedIn - opens share dialog
 */
export function shareToLinkedIn(blob: Blob): void {
    // LinkedIn also doesn't support direct image upload via URL
    downloadImage(blob, 'finite-life-weeks.png');
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://finite.eleventh-photon.com')}`;
    window.open(url, '_blank', 'width=600,height=500');
    showToast('Image downloaded! Share it on LinkedIn.');
}

/**
 * Check if Web Share API is available
 */
export function canUseWebShare(): boolean {
    return 'share' in navigator && 'canShare' in navigator;
}

/**
 * Share via Web Share API (for mobile)
 */
export async function shareViaWebAPI(blob: Blob, title: string): Promise<boolean> {
    if (!canUseWebShare()) {
        return false;
    }

    try {
        const file = new File([blob], 'finite-life-weeks.png', { type: 'image/png' });
        const shareData: ShareData = {
            title,
            files: [file],
        };

        if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return true;
        }
    } catch (error) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed:', error);
    }
    return false;
}

/**
 * Show a toast notification
 */
export function showToast(message: string): void {
    // Remove existing toast if any
    const existing = document.querySelector('.share-toast');
    if (existing) {
        existing.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'share-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
        toast.classList.add('visible');
    });

    // Remove after 3 seconds
    setTimeout(() => {
        toast.classList.remove('visible');
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}
